import React, { useCallback, useEffect, useState } from "react";
import {
  FaBriefcase,
  FaEnvelope,
  FaFilePdf,
  FaLock,
  FaPhone,
  FaTrash
} from "react-icons/fa";
import { loadCareerVacancies, sendCareerAdminRequest } from "./careerApi";
import "./Career.css";

function AdminCareer() {
  const initialForm = {
    title: "",
    department: "",
    type: "Full Time",
    location: "",
    experience: "",
    description: "",
    requirements: "",
    deadline: ""
  };

  const [password, setPassword] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState("");
  const [authStatus, setAuthStatus] = useState("");
  const [applicationsStatus, setApplicationsStatus] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);

  const sendAdminRequest = useCallback(
    (payload) =>
      sendCareerAdminRequest({
        password,
        ...payload
      }),
    [password]
  );

  const loadVacancies = useCallback(() => {
    loadCareerVacancies()
      .then((data) => {
        setVacancies(Array.isArray(data.vacancies) ? data.vacancies : []);
        setStatus(
          data.localFallback
            ? "PHP API is not running locally. Vacancies are being shown from this browser."
            : ""
        );
      })
      .catch(() => {
        setStatus("Unable to load vacancies. Check PHP hosting setup.");
      });
  }, []);

  const loadApplications = useCallback(
    (showLoadingStatus = true) => {
      setIsLoadingApplications(true);

      if (showLoadingStatus) {
        setApplicationsStatus("");
      }

      sendAdminRequest({
        action: "listApplications"
      })
        .then((data) => {
          const loadedApplications = Array.isArray(data.applications)
            ? data.applications
            : [];

          setApplications(loadedApplications);
          setApplicationsStatus(
            loadedApplications.length > 0
              ? data.localFallback
                ? "Applications loaded from this browser."
                : "Applications loaded successfully."
              : "No applications submitted yet."
          );
        })
        .catch((error) => {
          setApplicationsStatus(error.message);
        })
        .finally(() => {
          setIsLoadingApplications(false);
        });
    },
    [sendAdminRequest]
  );

  useEffect(() => {
    if (isUnlocked) {
      loadVacancies();
      loadApplications(false);
    }
  }, [isUnlocked, loadApplications, loadVacancies]);

  useEffect(() => {
    if (!isUnlocked) {
      return undefined;
    }

    const refreshApplications = () => loadApplications(false);

    window.addEventListener("focus", refreshApplications);
    window.addEventListener("storage", refreshApplications);

    return () => {
      window.removeEventListener("focus", refreshApplications);
      window.removeEventListener("storage", refreshApplications);
    };
  }, [isUnlocked, loadApplications]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value
    }));
  };

  const handleAdminLogin = (event) => {
    event.preventDefault();
    setIsVerifying(true);
    setAuthStatus("");

    sendAdminRequest({
      action: "verifyAdmin"
    })
      .then(() => {
        setIsUnlocked(true);
        setAuthStatus("");
      })
      .catch((error) => {
        setAuthStatus(error.message);
      })
      .finally(() => {
        setIsVerifying(false);
      });
  };

  const handlePostVacancy = (event) => {
    event.preventDefault();
    setIsSaving(true);
    setStatus("");

    sendAdminRequest({
      action: "add",
      vacancy: formData
    })
      .then((data) => {
        setVacancies(Array.isArray(data.vacancies) ? data.vacancies : []);
        setFormData(initialForm);
        setStatus(
          data.localFallback
            ? "Vacancy saved in this browser. Install PHP or use PHP hosting to publish it for everyone."
            : "Vacancy posted successfully."
        );
      })
      .catch((error) => {
        setStatus(error.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleDeleteVacancy = (vacancyId) => {
    setStatus("");

    sendAdminRequest({
      action: "delete",
      id: vacancyId
    })
      .then((data) => {
        setVacancies(Array.isArray(data.vacancies) ? data.vacancies : []);
        setStatus(
          data.localFallback
            ? "Vacancy deleted from this browser."
            : "Vacancy deleted successfully."
        );
      })
      .catch((error) => {
        setStatus(error.message);
      });
  };

  const handleLoadApplications = () => {
    loadApplications();
  };

  return (
    <div className="career-wrapper">
      <section className="career-hero admin-career-hero">
        <div className="career-hero-content">
          <span className="career-label">ADMIN</span>
          <h1>
            Manage Job <span className="career-gradient-text">Vacancies</span>
          </h1>
          <p>
            This page is for RDS Technology owner/admin to post and remove
            career vacancies from the public website.
          </p>
        </div>
      </section>

      <section className="career-admin-panel admin-career-panel">
        {!isUnlocked ? (
          <div className="career-admin-login">
            <div className="career-admin-heading">
              <span>Admin Login</span>
              <h2>Enter Password</h2>
              <p>
                Verify the admin password first to manage vacancies and view
                applied forms.
              </p>
            </div>

            <form className="career-vacancy-form" onSubmit={handleAdminLogin}>
              <div className="career-input-group admin-password-group">
                <label>
                  <FaLock /> Admin Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter admin password"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="career-submit-btn"
                disabled={isVerifying}
              >
                {isVerifying ? "Checking..." : "Enter Admin Panel"}
              </button>

              {authStatus && <p className="career-admin-status">{authStatus}</p>}
            </form>
          </div>
        ) : (
          <>
        <div className="career-admin-heading">
          <span>Secure Posting</span>
          <h2>Post a New Job Vacancy</h2>
          <p>
            Add new openings and review submitted application forms from this
            admin panel.
          </p>
        </div>

        <form className="career-vacancy-form" onSubmit={handlePostVacancy}>
          <div className="career-form-grid">
            <div className="career-input-group">
              <label>Job Title</label>
              <input
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Example: XML Production Executive"
                required
              />
            </div>

            <div className="career-input-group">
              <label>Department</label>
              <input
                name="department"
                type="text"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="Example: XML / Development"
                required
              />
            </div>

            <div className="career-input-group">
              <label>Job Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option>Full Time</option>
                <option>Part Time</option>
                <option>Internship</option>
                <option>Contract</option>
              </select>
            </div>

            <div className="career-input-group">
              <label>Location</label>
              <input
                name="location"
                type="text"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Example: Kulasekharam"
                required
              />
            </div>

            <div className="career-input-group">
              <label>Experience</label>
              <input
                name="experience"
                type="text"
                value={formData.experience}
                onChange={handleInputChange}
                placeholder="Example: 0-2 Years"
                required
              />
            </div>

            <div className="career-input-group">
              <label>Last Date</label>
              <input
                name="deadline"
                type="date"
                value={formData.deadline}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="career-input-group">
            <label>Job Description</label>
            <textarea
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Write the main responsibility and role summary."
              required
            ></textarea>
          </div>

          <div className="career-input-group">
            <label>Requirements</label>
            <textarea
              name="requirements"
              rows="4"
              value={formData.requirements}
              onChange={handleInputChange}
              placeholder="Add skills, qualification, shift timing, or other requirements."
              required
            ></textarea>
          </div>

          <button type="submit" className="career-submit-btn" disabled={isSaving}>
            {isSaving ? "Posting..." : "Post Vacancy"}
          </button>

          {status && <p className="career-admin-status">{status}</p>}
        </form>

        <div className="career-admin-list">
          <h3>Posted Vacancies</h3>
          {vacancies.length > 0 ? (
            vacancies.map((job) => (
              <div className="career-admin-job" key={job.id}>
                <div>
                  <strong>{job.title}</strong>
                  <span>
                    {job.department} | {job.type}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteVacancy(job.id)}
                  aria-label={`Delete ${job.title}`}
                >
                  <FaTrash />
                </button>
              </div>
            ))
          ) : (
            <div className="career-empty-state admin-empty-state">
              <FaBriefcase />
              <h3>No vacancies posted yet</h3>
              <p>Use the form above to publish the first job vacancy.</p>
            </div>
          )}
        </div>

        <div className="career-admin-list">
          <div className="career-admin-list-header">
            <h3>Applied Forms</h3>
            <button
              type="button"
              className="career-small-action"
              onClick={handleLoadApplications}
              disabled={isLoadingApplications}
            >
              {isLoadingApplications ? "Loading..." : "Load Applications"}
            </button>
          </div>
          {applicationsStatus && (
            <p className="career-admin-status">{applicationsStatus}</p>
          )}
          {applications.length > 0 ? (
            applications.map((application) => (
              <div className="career-admin-application" key={application.id}>
                <div className="career-admin-application-top">
                  <div>
                    <strong>{application.name}</strong>
                    <span>{application.jobTitle}</span>
                  </div>
                  <small>Applied {application.appliedDate}</small>
                </div>
                <div className="career-application-contact">
                  <span>
                    <FaEnvelope /> {application.email}
                  </span>
                  <span>
                    <FaPhone /> {application.phone}
                  </span>
                </div>
                {application.resumeFile?.dataUrl ? (
                  <a
                    href={application.resumeFile.dataUrl}
                    download={application.resumeFile.name || "resume.pdf"}
                    target="_blank"
                    rel="noreferrer"
                    className="career-resume-link"
                  >
                    <FaFilePdf /> View Resume PDF
                  </a>
                ) : application.resumeLink ? (
                  <a
                    href={application.resumeLink}
                    target="_blank"
                    rel="noreferrer"
                    className="career-resume-link"
                  >
                    View Resume
                  </a>
                ) : null}
                {application.message && <p>{application.message}</p>}
              </div>
            ))
          ) : (
            <div className="career-empty-state admin-empty-state">
              <FaBriefcase />
              <h3>No applications loaded</h3>
              <p>
                Submitted application forms will appear here after candidates
                apply.
              </p>
            </div>
          )}
        </div>
          </>
        )}
      </section>
    </div>
  );
}

export default AdminCareer;
